import { getCountByStatus, useGetAllWritings } from "@/api/writing/writing";
import NewItemDialogTrigger from "@/components/modal/NewItemDialogTrigger";
import StatusTableTab from "@/components/StatusTableTab";
import { AdvancedSearch } from "@/components/table/AdvancedSearch";
import { AdvancedSearchChip } from "@/components/table/AdvancedSearchChip";
import { AdvancedSort } from "@/components/table/AdvancedSort";
import { GlobalSearch } from "@/components/table/GlobalSearch";
import { DataTable } from "@/components/ui/datatable";
import NewWritingDialog from "@/components/writing/NewWritingDialog";
import { ColumnFormatted, generateTable } from "@/config/dashboard/general";
import {
  writingsAdvancedSearchConfig,
  writingsColumns,
} from "@/config/dashboard/writings";
import { useDashboardParams } from "@/hooks/useDashboardParams";
import { Writing } from "@/lib/types";
import { ColumnDef, VisibilityState } from "@tanstack/react-table";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

const STORAGE_KEY = "writing-table-preferences";

const flatVisibleWritingColumns = writingsColumns.flatMap(
  (column: ColumnFormatted) => (column.visible ? column.key : []),
);

const WritingDashboard = () => {
  const { t } = useTranslation();

  const {
    fetchParams,
    filterStatus,
    setFilterStatus,
    pagination,
    setPagination,
    countByStatus,
    currentCount,
  } = useDashboardParams<Writing>({
    resourceName: "writing",
    resourceCounter: getCountByStatus,
    statusField: "ecritsPublicationStatus",
  });

  const navigate = useNavigate();

  const { data } = useGetAllWritings(fetchParams);

  const columns: ColumnDef<Writing, any>[] = useMemo(() => {
    return generateTable(writingsColumns, "writing");
  }, []);

  const [columnsOrder, setColumnsOrder] = useState<string[]>(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      const { order } = JSON.parse(saved);
      return order || flatVisibleWritingColumns;
    }
    return flatVisibleWritingColumns;
  });

  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(
    () => {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const { visibility } = JSON.parse(saved);
        return (
          visibility || {
            ...writingsColumns.reduce(
              (acc, column) => ({
                ...acc,
                [column.key]: column.visible || false,
              }),
              {},
            ),
          }
        );
      }
      return {
        ...writingsColumns.reduce(
          (acc, column) => ({
            ...acc,
            [column.key]: column.visible || false,
          }),
          {},
        ),
      };
    },
  );

  return (
    <div className="flex flex-col items-start gap-3">
      <div className="flex justify-between  items-center w-full">
        <p className="text-[32px] font-[600]">{t("dashboardTitleWrittings")}</p>
        <GlobalSearch config={writingsAdvancedSearchConfig} data={data ?? []} />
      </div>
      <div className="flex flex-row justify-between w-full">
        <StatusTableTab
          filterStatus={filterStatus}
          countByStatus={countByStatus}
          onValueChange={setFilterStatus}
        />
        <div className="flex flex-row gap-2">
          <AdvancedSearch
            config={writingsAdvancedSearchConfig}
            resource="writing"
          />
          <AdvancedSort config={writingsAdvancedSearchConfig} />
          <NewItemDialogTrigger
            title={t("createWwriting")}
            Dialog={NewWritingDialog}
          />
        </div>
      </div>
      <div className="w-full flex flex-row">
        <AdvancedSearchChip />
      </div>
      <div className="w-full">
        <DataTable
          onRowClick={(row) => navigate(`/edit/writing/${row.original.id}`)}
          pagination={{
            pagination: pagination,
            setPage: setPagination,
            totalSize: currentCount,
          }}
          columns={columns}
          data={data ?? []}
          storageKey={STORAGE_KEY}
          options={{
            enableColumnResizing: true,
            state: {
              columnVisibility: columnVisibility,
              columnOrder: columnsOrder,
            },
            onColumnVisibilityChange: setColumnVisibility,
            onColumnOrderChange: setColumnsOrder,
          }}
        />
      </div>
    </div>
  );
};

export default WritingDashboard;
